import { type JSX, Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SubconfigurationVariantNavigationProps } from "@plansoft-configuration-renderer/components";
import {
  useCsAttributes,
  useSubstructureApi,
} from "@plansoft-configuration-renderer/framework";

import { useSubstructureStandaloneContentCtx } from "@/renderer/SubstructureStandaloneContentCtx";
import { customPalette } from "@/theme/theme";

import { CustomSubconfigurationResetButton } from "./CustomSubconfigurationButton";
import { CustomSubconfigurationCompleteIndicatorIcon } from "./CustomSubconfigurationCompleteIndicatorIcon";

const Tabs = lazy(() => import("@mui/material/Tabs"));
const Tab = lazy(() => import("@mui/material/Tab"));

/**
 * Tab control on configuration level
 * @category UI
 * @param props
 * @returns
 */

export function CustomSubconfigurationTabsNavigation(
  props: SubconfigurationVariantNavigationProps,
): JSX.Element {
  const {
    items,
    activePath,
    setActivePath: setActiveItem,
    navigationStyle,
  } = props;

  const { selectedSubstructure } = useSubstructureStandaloneContentCtx();

  const { resetSubCfgObject } = useSubstructureApi();

  const Attributes = useCsAttributes();

  const { t } = useTranslation();

  function handleReset(): void {
    resetSubCfgObject({ path: selectedSubstructure?.path });
  }

  return (
    <Suspense>
      <Box
        bgcolor={customPalette.primary.light}
        display={"flex"}
        flexDirection={"column"}
        height="100%"
      >
        {selectedSubstructure && (
          <Tab
            key={selectedSubstructure.id}
            className="tab-header"
            label={
              <Typography fontWeight={500}>
                {selectedSubstructure.name}
              </Typography>
            }
          />
        )}
        <Tabs
          variant="scrollable"
          orientation={props.orientation}
          onChange={(_, newValue) => setActiveItem(newValue)}
          value={activePath}
          sx={{
            height: "100%",
            ...navigationStyle,
            position: "inherit",
            maxWidth: "20rem",
          }}
        >
          {items.map((item) => {
            const activeAttribute = Attributes.find(
              (attribute) => attribute.path === item.path,
            );
            const selectedAttribute = activeAttribute?.values?.find(
              (value) => value.isSelected,
            );

            const selectedAttributeIds = item.custom?.showValueIds;
            const attributes = Attributes.filter(
              (attribute) =>
                Array.isArray(selectedAttributeIds) &&
                selectedAttributeIds.includes(attribute.id),
            );
            const selectedValues = attributes.reduce<string[]>(
              (values, attribute) => {
                const selected = attribute?.values?.find(
                  (value) => value.isSelected,
                );
                const selectedValue =
                  attribute.dataType === "String"
                    ? selected?.name
                    : selected?.value;
                if (selectedValue) {
                  const unit =
                    attribute.dataType === "Integer"
                      ? " " + attribute.unitSymbol
                      : "";
                  values.push(selectedValue + unit);
                }
                return values;
              },
              [],
            );

            return (
              <Tab
                key={item.path}
                value={item.path}
                className={
                  item.type === "ConfigurationAttribute"
                    ? selectedAttribute
                      ? "complete"
                      : ""
                    : item.isComplete
                      ? "complete"
                      : ""
                }
                label={
                  item.custom?.showValueIds ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        textAlign: "left",
                        overflow: "hidden",
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {item.label}
                      </Typography>
                      <Typography
                        variant="h6"
                        title={selectedValues.join(" - ")}
                        sx={{
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedValues.join(" - ")}
                      </Typography>
                    </Box>
                  ) : selectedAttribute?.name || selectedAttribute?.id ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        textAlign: "left",
                        overflow: "hidden",
                      }}
                    >
                      <Typography variant="body2">{item.label}</Typography>
                      <Typography
                        variant="h6"
                        title={
                          selectedAttribute.name ?? selectedAttribute.value
                        }
                        sx={{
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {selectedAttribute.name ?? selectedAttribute.value}
                        {selectedAttribute?.unitSymbol}
                      </Typography>
                    </Box>
                  ) : (
                    <>{item.label}</>
                  )
                }
                icon={
                  <CustomSubconfigurationCompleteIndicatorIcon
                    isComplete={
                      item.type === "ConfigurationAttribute"
                        ? selectedAttribute
                          ? true
                          : false
                        : item.isComplete
                    }
                  />
                }
              />
            );
          })}
          {/* Empty tab to create a small gap to ResetButton */}
          <Tab
            label=" " // Empty label to have same styling as other tabs
            icon={
              <CustomSubconfigurationCompleteIndicatorIcon isComplete={false} /> // inComplete icon to have same styling as other tabs
            }
          />
        </Tabs>
        <CustomSubconfigurationResetButton
          onResetClicked={handleReset}
          label={t("regiolux:reset.configuration")}
        />
      </Box>
    </Suspense>
  );
}
